import { useCallback, useEffect, useRef, useState } from "react";

import useI18n from "@I18n/hooks/useI18n";

/**
 * Browser-native dictation on top of the Web Speech API. Streams interim text while the
 * client speaks and hands back finalized segments so the caller can append them.
 *
 * SpeechRecognition is not in lib.dom, hence the local types. No polyfill: unsupported
 * browsers (Firefox) report `supported: false` and the caller hides the mic.
 */

type SpeechRecognitionResult = {
  isFinal: boolean;
  0: { transcript: string };
};

type SpeechRecognitionEvent = {
  resultIndex: number;
  results: { length: number } & Record<number, SpeechRecognitionResult>;
};

type SpeechRecognition = {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onend: (() => void) | null;
  onerror: ((event: { error: string }) => void) | null;
};

type SpeechRecognitionWindow = Window & {
  webkitSpeechRecognition?: new () => SpeechRecognition;
  SpeechRecognition?: new () => SpeechRecognition;
};

/** Errors that no amount of restarting fixes — stop instead of looping on them. */
const FATAL_ERRORS = [
  "not-allowed",
  "service-not-allowed",
  "audio-capture",
  "language-not-supported",
];

/**
 * Errors that prove the feature is not really available here, whatever the
 * constructor check said: some Chromium builds (Brave, Electron, distro builds
 * without Google's API key) expose `SpeechRecognition` and then refuse at
 * runtime. Stop offering the mic for the rest of the session.
 */
const UNSUPPORTED_ERRORS = ["service-not-allowed", "language-not-supported"];

/** Codes the client caused or that fire on every quiet pause — never surfaced. */
const SILENT_ERRORS = ["aborted", "no-speech"];

/** A forgotten mic should not stay hot indefinitely. */
const MAX_LISTENING_MS = 2 * 60 * 1000;

/**
 * Chrome on Android ignores `continuous` and ends the session on the first
 * pause. Restarting it from `onend` is a call with no user gesture behind it,
 * which re-opens the mic permission prompt every single time and ends in a
 * hard `not-allowed` once Chrome decides the page is nagging. One tap is one
 * burst there; every other engine keeps the auto-restart.
 */
const isAndroid = () =>
  typeof navigator !== "undefined" && /Android/i.test(navigator.userAgent);

const getRecognitionConstructor = () => {
  const speechWindow = window as SpeechRecognitionWindow;

  return speechWindow.webkitSpeechRecognition ?? speechWindow.SpeechRecognition;
};

/** Dictation continues an existing draft, so glue the new segment on with one space. */
export const appendDictation = (current: string, segment: string): string => {
  const addition = segment.trim();

  if (!addition) return current;
  if (!current.trim()) return addition;

  return `${current.replace(/\s+$/, "")} ${addition}`;
};

type UseSpeechDictationOptions = {
  onSegment: (segment: string) => void;
};

/**
 * Temporary kill switch (RT-4496): dictation has a bug, so the mic is hidden for
 * every browser the same way it is hidden on Firefox — `supported: false`. Flip
 * back to `false` once the bug is fixed.
 */
const DICTATION_TEMPORARILY_DISABLED = false;

export const useSpeechDictation = ({ onSegment }: UseSpeechDictationOptions) => {
  const { locale } = useI18n();
  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [runtimeUnsupported, setRuntimeUnsupported] = useState(false);

  const recognitionRef = useRef<SpeechRecognition | null>(null);
  /** Set when the client hits stop, so `onend` knows not to restart. */
  const stoppedByUserRef = useRef(true);
  const interimRef = useRef("");
  const onSegmentRef = useRef(onSegment);
  const maxDurationTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  onSegmentRef.current = onSegment;

  const supported =
    !DICTATION_TEMPORARILY_DISABLED &&
    typeof window !== "undefined" &&
    !!getRecognitionConstructor() &&
    !runtimeUnsupported;

  useEffect(() => {
    const Recognition = getRecognitionConstructor();

    if (!Recognition) return undefined;

    const recognition = new Recognition();

    // Chrome on Android re-delivers every finalized result in continuous mode,
    // so each word lands several times — and it ends on the first pause anyway.
    recognition.continuous = !isAndroid();
    recognition.interimResults = true;
    recognition.onresult = (event) => {
      if (stoppedByUserRef.current) return;

      let pending = "";

      for (let index = event.resultIndex; index < event.results.length; index += 1) {
        const result = event.results[index];

        if (result.isFinal) {
          onSegmentRef.current(result[0].transcript);
        } else {
          pending += result[0].transcript;
        }
      }

      interimRef.current = pending;
      setInterim(pending);
    };

    recognition.onerror = ({ error: errorCode }) => {
      // `aborted` fires on every stop and on unmount; `no-speech` on every
      // pause to think. Both are expected, neither is worth a toast.
      if (SILENT_ERRORS.includes(errorCode)) return;

      if (FATAL_ERRORS.includes(errorCode)) stoppedByUserRef.current = true;
      if (UNSUPPORTED_ERRORS.includes(errorCode)) setRuntimeUnsupported(true);

      setError(errorCode);
    };

    // Mobile Safari ends sessions on its own; restart unless the client asked
    // to stop — except on Android, see `isAndroid`.
    recognition.onend = () => {
      const pending = interimRef.current;

      interimRef.current = "";
      setInterim("");

      if (stoppedByUserRef.current) {
        setListening(false);

        return;
      }

      if (isAndroid()) {
        // The browser ended it, not the client, so nothing has flushed the
        // interim text yet — keep what was said before dropping the session.
        if (pending) onSegmentRef.current(pending);
        stoppedByUserRef.current = true;
        clearTimeout(maxDurationTimeoutRef.current);
        setListening(false);

        return;
      }

      try {
        recognition.start();
      } catch {
        setListening(false);
      }
    };

    recognitionRef.current = recognition;

    return () => {
      clearTimeout(maxDurationTimeoutRef.current);
      stoppedByUserRef.current = true;
      recognition.abort();
      recognitionRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (recognitionRef.current)
      recognitionRef.current.lang = locale;
  }, [locale]);

  const stop = useCallback(() => {
    clearTimeout(maxDurationTimeoutRef.current);
    if (interimRef.current) onSegmentRef.current(interimRef.current);
    stoppedByUserRef.current = true;
    interimRef.current = "";
    recognitionRef.current?.stop();
    setListening(false);
    setInterim("");
  }, []);

  const start = useCallback(() => {
    if (!recognitionRef.current) return;

    setError(null);
    stoppedByUserRef.current = false;

    try {
      recognitionRef.current.start();
      setListening(true);
    } catch {
      // Already running: nothing to do.
      setListening(true);
    }

    // A forgotten mic should not stay hot indefinitely.
    clearTimeout(maxDurationTimeoutRef.current);
    maxDurationTimeoutRef.current = setTimeout(stop, MAX_LISTENING_MS);
  }, [stop]);

  const toggle = useCallback(() => {
    if (listening) {
      stop();
    } else {
      start();
    }
  }, [listening, start, stop]);

  return { supported, listening, interim, error, start, stop, toggle };
};
